import { get, merge, some } from 'lodash';

import {
  ENTITY_TYPES,
  IModalDialogOptions,
  IOrderAddPendingInventoryItemVenue,
  ObjectId,
  WIDGET_COMPONENT,
} from '@app/shared/types';
import { BTN_CANCEL, BTN_YES } from '@app/text-content/button-templates';
import { OrderAddPendingFormService } from './order-add-pending-form.service';
import {
  INPUT_MAX_LENGTH,
  MAX_SEVEN_DIGITS_NUMBER,
  WIDGET_FAMILY,
} from '@config/global-constants';
import { generateMaximumByPreferencesPrecision } from '@app/shared/utils';
import { I18n } from '@app/i18n/strings-keys';
import { XVII18nService } from '@app/i18n/services';
import { getSingularEntityName } from '@app/shared/configs';

export const createOrderAddPendingModal = (
  formService: OrderAddPendingFormService,
  dataOption: {
    inventoryItem: IOrderAddPendingInventoryItemVenue;
    code: string;
    cost: number;
    defaultMeasureEntityId: ObjectId;
  },
): IModalDialogOptions => {
  const i18nService: XVII18nService = formService.i18nService;

  const currencyPrecision =
    formService.countryFormatService.getCurrencyPrecision();
  const { inventoryItem, code, cost, defaultMeasureEntityId } = dataOption;
  const disabled: boolean = some(inventoryItem.vendors, {
    entity_id: formService.document.vendor_entity_id,
  });

  return merge(
    {},
    {
      title: i18nService.instant(I18n.Forms.GridForm.EditEntity, {
        entityType: i18nService.instant(
          getSingularEntityName(ENTITY_TYPES.INVENTORY_ITEM),
        ),
      }),
      subTitle: get(inventoryItem, 'name'),
      validation: true,
      buttons: [
        {
          ...BTN_YES,
          caption: I18n.Forms.GridForm.SaveAndAdd,
        },
        {
          ...BTN_CANCEL,
        },
      ],
      value: {
        [formService.addVendorFormFieldPath.name]: get(formService.document, [
          'vendor_name',
        ]),
        [formService.addVendorFormFieldPath.vendor_item_code]: code,
        [formService.addVendorFormFieldPath.measure_entity_id]:
          defaultMeasureEntityId,
        [formService.addVendorFormFieldPath.cost]: cost,
        [formService.addVendorFormFieldPath.site_availability]: true,
      },
      schema: {
        type: 'object',
        properties: {
          [formService.addVendorFormFieldPath.name]: {
            type: 'string',
          },
          [formService.addVendorFormFieldPath.vendor_item_code]: {
            type: 'string',
          },
          [formService.addVendorFormFieldPath.measure_entity_id]: {
            type: ['string', 'object'],
          },
          [formService.addVendorFormFieldPath.cost]: {
            type: 'number',
          },
          [formService.addVendorFormFieldPath.site_availability]: {
            type: 'boolean',
          },
        },
      },
      formRules: {
        fields: {
          [formService.addVendorFormFieldPath.name]: {
            title: I18n.Forms.GridForm.VendorName,
            widgetFamily: WIDGET_FAMILY.XUX,
            required: true,
            disabled: true,
          },
          [formService.addVendorFormFieldPath.vendor_item_code]: {
            widgetFamily: WIDGET_FAMILY.XUX,
            title: I18n.Forms.GridForm.VendorItemCode,
            type: 'string',
            disabled,
            description: I18n.InfoMessages.VendorAssignProductItemCode,
            trim: true,
            maxInputLength: INPUT_MAX_LENGTH,
          },
          [formService.addVendorFormFieldPath.measure_entity_id]: {
            title: I18n.Forms.GridForm.OrderingUnit,
            type: 'string',
            widgetFamily: WIDGET_FAMILY.XUX,
            widget: WIDGET_COMPONENT.select,
            required: true,
            description: I18n.InfoMessages.DefaultPurchaseOrderingUnit,
            selectOptions: formService.measuresForVendor,
          },
          [formService.addVendorFormFieldPath.cost]: {
            title: I18n.Forms.GridForm.UnitCost,
            type: disabled ? 'text' : 'number',
            widgetFamily: WIDGET_FAMILY.XUX,
            widget: WIDGET_COMPONENT.input_currency,
            required: true,
            disabled,
            hideArrows: true,
            defaultValue: null,
            minimum: 0,
            maximum: generateMaximumByPreferencesPrecision(
              MAX_SEVEN_DIGITS_NUMBER,
              currencyPrecision,
            ),
            widgetData: {
              precisionFn: () => currencyPrecision,
              autoPaddingZero: false,
              stopPrecisionValidator: disabled,
              notRoundInitValue: true,
              numberMultipleOfValidatorFn: !disabled
                ? formService.validationService.outOfPrecisionCurrencyData
                : null,
            },
          },
          [formService.addVendorFormFieldPath.site_availability]: {
            title: I18n.Forms.GridForm.SiteAvailability,
            widget: WIDGET_COMPONENT.switch,
            widgetFamily: WIDGET_FAMILY.XUX,
            disabled: true,
            defaultValue: true,
            class: 'switch-full-width',
            widgetData: {
              checkedText: `${get(formService.document, ['site_name'])} [${get(formService.document, ['store_number'])}]`,
            },
          },
        },
        defaultGroup: true,
      },
    },
  );
};
